import { useState } from "react";
import type { CSSProperties, FocusEventHandler, ReactNode } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

// antd <InputNumber> -> MUI TextField. antd onChange(value:number|null);
// MUI onChange(event) -> we parse and forward the numeric value.

// What a number can look like part-way through being typed: "-", "1.", "-20." all
// match, because they are a prefix of a number the user is still entering.
const PARTIAL_NUMBER = /^-?\d*(\.\d*)?$/;

export interface InputNumberProps {
  value?: number | null;
  defaultValue?: number;
  onChange?: (value: number | null) => void;
  // owned by this component (it clears the in-progress text); declared so an
  // injected handler -- Form.Item clones its child with one -- is chained, not
  // dropped into `rest` where the spread would silently replace ours.
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  disabled?: boolean;
  placeholder?: string;
  size?: "small" | "middle" | "large";
  style?: CSSProperties;
  className?: string;
  id?: string;
  autoFocus?: boolean;
  addonAfter?: ReactNode;
  addonBefore?: ReactNode;
  suffix?: ReactNode;
  prefix?: ReactNode;
  // accepted for antd compat, no direct MUI equivalent (ignored).
  // params are loose: call sites narrow to number; precise typing has no runtime value here.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  formatter?: (value?: any, info?: any) => string;
  parser?: (displayValue?: any) => any;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  controls?: boolean;
  status?: "error" | "warning" | "" | false;
  "data-testid"?: string;
}

export const InputNumber = ({
  value, defaultValue, onChange, onBlur, min, max, step, disabled, placeholder, size, style, className,
  autoFocus, addonAfter, addonBefore, suffix, prefix, status,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  precision, formatter, parser, controls, ...rest
}: InputNumberProps) => {
  // type="number" cannot hold a number that is still being typed: the browser
  // sanitizes "-" (and "1.") to "", so onChange reported null and the parent wrote
  // its own value back over the keystroke -- a negative was impossible to enter.
  // Hold the raw text while the field is being edited, report only what parses,
  // and drop the text on blur so the parent's value shows again.
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <TextField
      type="text"
      value={draft ?? value ?? ""}
      defaultValue={defaultValue}
      error={status === "error"}
      onChange={(e) => {
        const v = e.target.value;
        if (!PARTIAL_NUMBER.test(v)) return; // reject anything that is not a number
        setDraft(v);
        const parsed = v === "" ? null : Number(v);
        if (parsed === null || !Number.isNaN(parsed)) onChange?.(parsed);
      }}
      onBlur={e => {
        setDraft(null);
        onBlur?.(e);
      }}
      disabled={disabled}
      placeholder={placeholder}
      autoFocus={autoFocus}
      size={size === "large" ? "medium" : "small"}
      // antd InputNumber defaults to width:100%; fill the container. Explicit style.width wins.
      fullWidth
      style={style}
      className={className}
      InputProps={{
        startAdornment: (addonBefore ?? prefix) ? <InputAdornment position="start">{addonBefore ?? prefix}</InputAdornment> : undefined,
        endAdornment: (addonAfter ?? suffix) ? <InputAdornment position="end">{addonAfter ?? suffix}</InputAdornment> : undefined,
      }}
      // min/max/step are inert on a text input; they stay for call sites that read
      // them off the DOM. inputMode keeps the numeric keypad on touch devices.
      inputProps={{ min, max, step, inputMode: "decimal" }}
      {...rest}
    />
  );
};

export default InputNumber;
